import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ── Production auth: httpOnly cookies for JWT (access + refresh).
// `withCredentials: true` tells the browser to send cookies on every /api call.
export const http = axios.create({
  baseURL: API,
  timeout: 60000,
  withCredentials: true,
});

// Attach CSRF double-submit token from the (non-httpOnly) bp_csrf cookie
function readCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : '';
}
http.interceptors.request.use((config) => {
  const csrf = readCookie('bp_csrf');
  if (csrf) config.headers['X-CSRF-Token'] = csrf;
  return config;
});

// ── Silent refresh interceptor: on 401, attempt one refresh + retry ──
let _refreshInFlight = null;
async function _tryRefresh() {
  if (!_refreshInFlight) {
    _refreshInFlight = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .then(() => true)
      .catch(() => false)
      .finally(() => { _refreshInFlight = null; });
  }
  return _refreshInFlight;
}

http.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const status = err?.response?.status;

    // Silent refresh on 401 — attempt once per original request
    if (status === 401 && !original._retried) {
      const path = original.url || '';
      // Never try to refresh the refresh endpoint itself, or auth endpoints during login/register
      const skip = /\/auth\/(refresh|login|register|logout|me)/.test(path);
      if (!skip) {
        original._retried = true;
        const ok = await _tryRefresh();
        if (ok) {
          try { return await http.request(original); } catch (e) { err = e; }
        }
      }
    }

    // Normalize network/gateway/timeout errors so callers get a user-safe .detail
    if (!err?.response) {
      err.response = err.response || {};
      err.response.data = err.response.data || {
        detail: err.code === 'ECONNABORTED'
          ? "That took longer than expected. Let's try again in a moment."
          : "I'm having trouble reaching the server right now. Let's try again in a moment.",
      };
    } else if (err.response.status >= 500 && (!err.response.data || !err.response.data.detail)) {
      err.response.data = { detail: "I'm having trouble with that right now. Let's try again in a moment." };
    }
    return Promise.reject(err);
  }
);
